import { Injectable } from '@angular/core';

import { ActivatedRoute, Router } from '@angular/router';
import { ConstantConfig } from '../config/constant.config';
import { UserInfo } from '../models/user/user-info.model';
import { ClientUtils } from '../utils/clientUtil';
import { Utility } from '../utils/utility';
import { ClientDetailService } from './client-detail-service';
import { EncryptDecryptService } from './encrypt-decrypt.service';
import { LocalStorageService } from './local-storage.service';

@Injectable()
export class UserService {

  constructor(private localStorageService: LocalStorageService,
    private edService:EncryptDecryptService, private activatedRoute: ActivatedRoute, private router :Router, private clientDetailService:ClientDetailService) { }


  /**
   * set user Object
   * @param- userObj
   */
  public setUserInfo(userObj: UserInfo) {
    const existingEmail = this.getSessionEmail();
    if ((!userObj?.email || userObj.email.includes('*')) && existingEmail) {
      userObj.email = existingEmail;
    }
    if (userObj?.email) {
      this.setSessionEmail(userObj.email);
    }
    const clientSlug = this.getStorageSlug();
    const info = this.edService.encrypt(JSON.stringify(userObj));
    this.localStorageService.set(clientSlug + "_" + ConstantConfig.USER_INFO, info);
  }

  /**
   * set user Object
   * @param- clientSlug
   * @param- userObj
   */
  public setClientUserInfo(clientSlug: string, userObj: UserInfo) {
    const info = this.edService.encrypt(JSON.stringify(userObj));
    this.localStorageService.set(clientSlug + "_" + ConstantConfig.USER_INFO, info);
  }

  /**
   * get user info
   * @param- field
   * @returns- {string}
   */
  getUserInfo(field: string = ''):string | UserInfo | any {
    const clientSlug = this.getStorageSlug();
    const userInfoObj = this.localStorageService.get(clientSlug + "_" + ConstantConfig.USER_INFO) || '';
    try {
      const info = this.edService.decrypt(userInfoObj);
      if (!info) {
        return field ? '' : '';
      }
      const parsed = typeof info === 'string' ? JSON.parse(info) : info;
      return field ? (parsed?.[field] ?? '') : parsed;
    } catch {
      return field ? '' : '';
    }
  }

  setSessionEmail(email: string): void {
    const normalized = this.normalizeEmail(email);
    const clientSlug = this.getStorageSlug();
    if (normalized && clientSlug) {
      sessionStorage.setItem(clientSlug + "_" + ConstantConfig.SESSION_EMAIL, normalized);
    }
  }

  getSessionEmail(): string {
    const clientSlug = this.getStorageSlug();
    const stored = clientSlug ? sessionStorage.getItem(clientSlug + "_" + ConstantConfig.SESSION_EMAIL) : '';
    return this.normalizeEmail(stored || this.getUserInfo('email') || this.getEmailFromToken() || '');
  }

  private getEmailFromToken(): string {
    const token = this.AUTH_TOKEN;
    if (!token || token === 'idle') {
      return '';
    }
    try {
      const parts = token.split('.');
      if (parts.length < 2) {
        return '';
      }
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      return payload.email || payload.user?.email || '';
    } catch {
      return '';
    }
  }

  private normalizeEmail(email: string): string {
    if (!email || typeof email !== 'string' || email.includes('*')) {
      return '';
    }
    const prefix = Utility.getLoginAsClientUserPrefix();
    return email.startsWith(prefix) ? email.slice(prefix.length) : email;
  }

  private getStorageSlug(): string {
    return ClientUtils.getClientSlug(this.activatedRoute, this.router)
      || this.clientDetailService.CLIENT_ID
      || this.getClientId()
      || '';
  }

/**
   * set user Permissions
   */
  setPermissions(clientSlug:string ,permissions:Array<string>){
    const info = permissions ? this.edService.encrypt(JSON.stringify(permissions)) : JSON.stringify([]);
    this.localStorageService.set(clientSlug + "_" + ConstantConfig.USER_PERMISSION, info);
  }

  /**
   * get user Permissions
   */
   get PERMISSIONS():Array<any> {
    //  user permission from local storage
    const clientSlug = ClientUtils.getClientSlug(this.activatedRoute, this.router);
    const userPermissions = this.localStorageService.get(clientSlug + "_" + ConstantConfig.USER_PERMISSION);
    const info :any = userPermissions ? JSON.parse(this.edService.decrypt(userPermissions)) : [];
    return info;
  }

  /**
   * get Auth Token
   * @returns- {string}
   * constructor
   */
  public get AUTH_TOKEN(): string {
    let clientSlug = ClientUtils.getClientSlug(this.activatedRoute, this.router);
    if(!clientSlug){
      clientSlug = this.clientDetailService.CLIENT_ID;
    }
    const token = this.localStorageService.get(clientSlug + "_"  + ConstantConfig.AUTH_TOKEN);
    return token ? token : '';
  }



  /**
   * Set Auth Token Expiry
   * @param- time
   */
   public setAuthTokenExpiry(time: string) {
    let clientSlug = ClientUtils.getClientSlug(this.activatedRoute, this.router);
    if(!clientSlug){
      clientSlug = this.clientDetailService.CLIENT_ID;
    }
    const timeInSecond = new Date(time).getTime();
    this.localStorageService.set(clientSlug + "_"  + ConstantConfig.AUTH_TOKEN_EXPIRY,timeInSecond);
  }

    /**
   * Set Client Auth Token Expiry
   * @param- time
   */
     public setClientAuthTokenExpiry(clientSlug:string,time: string) {
      const timeInSecond = new Date(time).getTime();
      this.localStorageService.set(clientSlug + "_"  + ConstantConfig.AUTH_TOKEN_EXPIRY,timeInSecond);
    }

  /**
   * Get Auth Token Expiry
   * @return - number
   */
   public getAuthTokenExpiry():number {
    let clientSlug = ClientUtils.getClientSlug(this.activatedRoute, this.router);
    if(!clientSlug){
      clientSlug = this.clientDetailService.CLIENT_ID;
    }
    const tokenExpiry = this.localStorageService.get(clientSlug + "_"  + ConstantConfig.AUTH_TOKEN_EXPIRY);
    return tokenExpiry ? tokenExpiry : 0;
  }

  /**
   * Set Auth Token
   * @param- authToken
   */
  public setAuthToken(authToken: string) {
    const clientSlug = ClientUtils.getClientSlug(this.activatedRoute, this.router);
    this.localStorageService.set(clientSlug + "_"  + ConstantConfig.AUTH_TOKEN,authToken);
  }

  /**
   * Set Auth Token
   * @param- clientSlug
   * @param- authToken
   */
  public setClientAuthToken(clientSlug:String, authToken: string) {
    this.localStorageService.set(clientSlug + "_"  + ConstantConfig.AUTH_TOKEN,authToken);
  }

  /**
   * Set Login status
   * @param- clientSlug
   */
  public setDirectLogin(clientSlug:String) {
    this.localStorageService.set(clientSlug + "_"  + ConstantConfig.LOGIN_AS_CLIENT, true);
  }

  /**
   * get login status
   * @param- clientSlug
   */
  public getLoginType(clientSlug:String) {
    const loginType = this.localStorageService.get(clientSlug + "_"  + ConstantConfig.LOGIN_AS_CLIENT);
    return loginType == 'true';
  }

  /**
   * getClientId
   */
  public getClientId() {
    let clientId = this.activatedRoute.snapshot.paramMap.get('clientId') || '';
    if(!clientId){
      let url = this.router.url.split("/");
      if(url.length > 1){
        clientId = url[1];
      }
    }
    return clientId;
  }
}
